import React, { useEffect, useContext } from 'react';
import GtmContext from '../../contexts/GtmContext';
import { SearchProductCardWrapper } from './SearchProductCardWrapper';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { LoadingSkeleton } from '@jbhi-fi/components/src/LoadingSkeleton/LoadingSkeleton';
import { getStoreCurrency } from '../../utils/shopify/getStoreCurrency';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { SessionStorageKeys, getSession } from '@jbhi-fi/utils/src/sessionStorage';
import { postAlgoliaProductClick, addInsight } from '../../utils/algolia/search-insights';
import { useHits, useInstantSearch } from 'react-instantsearch';
import PropTypes from 'prop-types';
import { useCartContext } from '../../utils/cart/context/CartContext';

export const ProductSuggestions = ({ setNbHits, priceRange, saleItems }) => {
  const { hits } = useHits();
  const { results, uiState, status } = useInstantSearch();
  const searching = status === 'loading';
  const isSearchStalled = status === 'stalled';
  const index = results?.index;
  const searchResults = results;
  const { cart } = useCartContext();
  const searchState = uiState[index];
  const { gtm, setGtm } = useContext(GtmContext);
  const hasResults = searchResults && searchResults.nbHits !== 0;
  const noResults = searchResults && searchResults.nbHits === 0 && !searching && !isSearchStalled;
  const PRODUCTS_LIMIT = 4;
  const isDesktop = useBreakpoint('md', 'up');
  const minPrice = priceRange?.min ? { 'Min Price': priceRange?.min } : null;
  const maxPrice = priceRange?.max ? { 'Max Price': priceRange?.max } : null;
  const saleItemsFilter = saleItems === true ? { 'Sale items': saleItems } : null;

  const filters =
    minPrice || maxPrice || saleItemsFilter
      ? {
          price: {
            ...minPrice,
            ...maxPrice,
          },
          ...saleItemsFilter,
        }
      : null;

  useEffect(() => {
    if (searchResults) {
      setNbHits(searchResults.nbHits);
    }
  }, [searchResults, setNbHits]);

  useEffect(() => {
    if (hasResults) {
      pushToDataLayer({
        event: 'view_search_results',
        count: searchResults.hits.length,
        searchTerm: searchState.query,
        searchType: 'site search',
        method: getSession(SessionStorageKeys.SearchMethod),
        item_list_name: `search - product suggestions`,
        ...(filters && { filters }),
      });
    }
  }, [priceRange, saleItems]);

  useEffect(() => {
    if (hasResults) {
      trackViewItemList({
        currency: getStoreCurrency(),
        listName: `search - product suggestions`,
        searchType: 'product suggestions',
        searchTerm: searchState.query,
        viewedItems: transformAlgoliaHitForTracking(hits),
      });

      setGtm({
        ...gtm,
        search: {
          ...gtm.search,
          count: searchResults.nbHits,
        },
        suggestions: {
          ...gtm.suggestions,
          products: hits.slice(0, PRODUCTS_LIMIT).map((c, i) => ({
            label: c.title,
            position: i + 1,
            price: c.price,
          })),
        },
        ecommerce: {
          currencyCode: getStoreCurrency(),
          impressions: hits.slice(0, PRODUCTS_LIMIT).map((c, i) => ({
            ...c.google_tag_manager,
            position: i + 1,
            list: 'search overlay',
          })),
        },
      });
    } else {
      setGtm({
        ...gtm,
        search: {
          ...gtm.search,
          count: 0,
        },
        suggestions: {
          ...gtm.suggestions,
          products: undefined,
        },
        ecommerce: {
          currencyCode: getStoreCurrency(),
          impressions: undefined,
        },
      });
    }
  }, [hasResults]);

  const clickGtmHandler = hit => {
    const gtmProduct = gtm?.ecommerce?.impressions?.find(product => product.name === hit.google_tag_manager.name);
    if (!gtmProduct) return;
    window.dataLayer.push({
      ...gtm,
      event: 'custom.search.suggested.click',
      label: hit.title,
      suggestions: undefined,
      info: {
        url: window.location.href,
      },
      suggestion: {
        type: 'product',
        label: gtmProduct.name,
        position: gtmProduct.position,
        price: gtmProduct.price,
      },
      ecommerce: {
        currencyCode: getStoreCurrency(),
        click: {
          actionField: {
            list: 'search overlay',
          },
          products: [
            {
              ...hit.google_tag_manager,
              position: gtmProduct.position,
              list: gtmProduct.list,
            },
          ],
        },
      },
    });
  };

  if (noResults) {
    return <div key="no-results">No results</div>;
  } else if (hasResults) {
    const productCards = hits.slice(0, PRODUCTS_LIMIT).map((hit, index) => (
      <SearchProductCardWrapper
        hit={hit}
        key={hit.objectID}
        onClick={sku => {
          clickGtmHandler(hit);
          addInsight(sku, hit.__queryID, cart);
          postAlgoliaProductClick(sku, index + 1, hit.__queryID);
        }}
        position={index + 1}
        type="modal"
        variant="small"
      />
    ));
    return (
      <>
        {isDesktop ? (
          productCards
        ) : (
          <Carousel showPagination={false} slidesPerView="auto" fullBleed>
            {productCards}
          </Carousel>
        )}
      </>
    );
  } else {
    return [...Array(PRODUCTS_LIMIT)].map((e, i) => (
      <LoadingSkeleton.ProductCardSkeleton shouldShimmer variant="small" key={i} />
    ));
  }
};

ProductSuggestions.propTypes = {
  setNbHits: PropTypes.func.isRequired,
  priceRange: PropTypes.object,
  saleItems: PropTypes.bool,
};
